import { z } from "zod";
import { safeHttpUrlSchema } from "../safe-url";

export const teachingLevelEnum = z.enum([
  "undergraduate",
  "postgraduate",
  "phd",
  "executive",
  "other",
]);

export const teachingRoleEnum = z.enum([
  "instructor",
  "co_instructor",
  "teaching_assistant",
  "guest_lecturer",
  "other",
]);

export const draftTeachingItemSchema = z.object({
  id: z.string().default(() => crypto.randomUUID()),
  courseCode: z.string().max(50), // empty allowed in draft
  courseTitle: z.string().max(255), // empty allowed in draft
  level: teachingLevelEnum.default("undergraduate"),
  role: teachingRoleEnum.default("instructor"),
  institution: z.string().max(255).optional(),
  term: z.string().max(100).optional(), // e.g. "Fall 2025", "Spring Semester"
  description: z.string().max(2000).optional(),
  courseUrl: z.string().max(1024).optional().or(z.literal("")).nullable(),
  syllabusUrl: z.string().max(1024).optional().or(z.literal("")).nullable(),
});

export const teachingItemSchema = z.object({
  id: z.string().default(() => crypto.randomUUID()),
  courseCode: z.string().trim().min(1, "Course code is required").max(50),
  courseTitle: z.string().trim().min(1, "Course title is required").max(255),
  level: teachingLevelEnum.default("undergraduate"),
  role: teachingRoleEnum.default("instructor"),
  institution: z.string().max(255).optional(),
  term: z.string().max(100).optional(),
  description: z.string().max(2000).optional(),
  courseUrl: safeHttpUrlSchema.or(z.literal("")).optional(),
  syllabusUrl: safeHttpUrlSchema.or(z.literal("")).optional(),
});

import { getSectionDisplayOptionsSchema } from "../display-options";

export const teachingDisplayOptionsSchema = getSectionDisplayOptionsSchema("teaching");
export type TeachingDisplayOptions = z.infer<typeof teachingDisplayOptionsSchema>;

export const draftTeachingContentSchema = z.object({
  heading: z.string().max(100).default("Teaching & Courses"),
  displayStyle: z.enum(["grouped_by_level", "grouped_by_year", "list"]).default("grouped_by_level"),
  items: z.array(draftTeachingItemSchema).max(50).default([]),
  displayOptions: teachingDisplayOptionsSchema.optional(),
});



export const teachingContentSchema = z.object({
  heading: z.string().trim().min(1).max(100).default("Teaching & Courses"),
  displayStyle: z.enum(["grouped_by_level", "grouped_by_year", "list"]).default("grouped_by_level"),
  items: z.array(teachingItemSchema).max(50).default([]),
  displayOptions: teachingDisplayOptionsSchema.optional(),
});

export type TeachingLevel = z.infer<typeof teachingLevelEnum>;
export type TeachingRole = z.infer<typeof teachingRoleEnum>;
export type TeachingItem = z.infer<typeof teachingItemSchema>;
export type DraftTeachingItem = z.infer<typeof draftTeachingItemSchema>;
export type TeachingContent = z.infer<typeof teachingContentSchema>;
export type DraftTeachingContent = z.infer<typeof draftTeachingContentSchema>;
